/**
 * Voluum-style “Traffic distribution AI (paths)” — linked to default paths row.
 */

import type { FC } from 'react';
import { useId } from 'react';
import type { VoluumFlowUpdate } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import { cn } from '@/lib/utils';

import {
  allSetsAutoOptimizedUniform,
  allSetsOptimizationStats,
  defaultPathsCalculationMethodUniform,
  setAllSetsAutoOptimized,
  setAllSetsCalculationMethod,
} from '../../../utils/default-paths-optimization';

import CalculationMethodSelect from './CalculationMethodSelect';

const CARD_HINT =
  'Master toggle: applies to the default-paths set AND every rule-based group. Voluum counts each as a "set" (e.g. "2/2 sets of paths optimized"). Use the per-group switch for granular control.';

export interface TrafficDistributionAiPathsCardProps {
  draft: VoluumFlowUpdate;
  onPatch: (mutator: (d: VoluumFlowUpdate) => void) => void;
}

function formatStatus(total: number, enabled: number, allOn: boolean): string {
  if (total === 0) return 'No paths';
  if (allOn) return `Optimization enabled — ${enabled}/${total} sets`;
  if (enabled === 0) return `Optimization disabled — 0/${total} sets`;
  return `Partial — ${enabled}/${total} sets on`;
}

const TrafficDistributionAiPathsCard: FC<
  TrafficDistributionAiPathsCardProps
> = ({ draft, onPatch }) => {
  const checkboxId = useId();
  const { total, enabled } = allSetsOptimizationStats(draft);
  const disabled = total === 0;
  const uniform = allSetsAutoOptimizedUniform(draft);
  const checked: boolean | 'indeterminate' =
    uniform === 'allOn'
      ? true
      : uniform === 'mixed'
        ? 'indeterminate'
        : false;
  const methodU = defaultPathsCalculationMethodUniform(draft);
  const status = formatStatus(total, enabled, uniform === 'allOn');

  return (
    <section
      aria-label="Traffic distribution AI for default paths"
      className={cn(
        'rounded-md border border-border/70 bg-muted/10 px-2 py-1',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id={checkboxId}
            className="h-3.5 w-3.5 shrink-0"
            checked={checked}
            disabled={disabled}
            onCheckedChange={(state) =>
              onPatch((d) => setAllSetsAutoOptimized(d, state === true))
            }
            aria-label="Traffic distribution AI for default paths"
          />
          <label
            htmlFor={checkboxId}
            className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5"
          >
            <Tooltip content={CARD_HINT}>
              <span className="w-fit text-[11px] font-semibold leading-snug text-foreground">
                Traffic distribution AI (paths)
              </span>
            </Tooltip>
            <span className="text-[11px] leading-snug text-muted-foreground">
              {status}
            </span>
          </label>
        </div>
        {enabled > 0 ? (
          <div className="w-full pl-6">
            <CalculationMethodSelect
              className="w-full max-w-[14rem] [&_select]:max-w-none [&_select]:w-full"
              value={methodU}
              disabled={disabled}
              onChange={(v) =>
                onPatch((d) => setAllSetsCalculationMethod(d, v))
              }
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default TrafficDistributionAiPathsCard;
